'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import DrawingCanvas from '@/components/editor/Canvas';
import Toolbar from '@/components/editor/Toolbar';
import PropertiesPanel from '@/components/editor/PropertiesPanel';
import {
  DrawingObject,
  Tool,
  SnapSize,
  DEFAULT_SNAP_SIZE,
  DEFAULT_LAYERS,
  Layer as LayerType,
} from '@/types';
import {
  isSupabaseConfigured,
  saveProjectAuto,
  loadProject,
  loadFromLocalStorage,
  saveToLocalStorage,
  listProjects,
  deleteProject,
  type ProjectSummary,
} from '@/lib/projects';

const MAX_HISTORY = 20;
const AUTOSAVE_INTERVAL = 30_000; // 30 seconds

export default function EditorPage() {
  // Drawing state
  const [objects, setObjects] = useState<DrawingObject[]>([]);
  const [activeTool, setActiveTool] = useState<Tool>('select');
  const [snapSize, setSnapSize] = useState<SnapSize>(DEFAULT_SNAP_SIZE);
  const [showGrid, setShowGrid] = useState(true);
  const [selectedWallType, setSelectedWallType] = useState('ext-standard');
  const [selectedObject, setSelectedObject] = useState<DrawingObject | null>(null);
  const [layers, setLayers] = useState<LayerType[]>(DEFAULT_LAYERS);

  // History for undo/redo
  const [history, setHistory] = useState<DrawingObject[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Project state
  const [projectName, setProjectName] = useState('Nytt projekt');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [projectId, setProjectId] = useState<string | undefined>(undefined);

  // Project list (Supabase only)
  const [showProjectList, setShowProjectList] = useState(false);
  const [projectList, setProjectList] = useState<ProjectSummary[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);

  // Dirty flag for autosave
  const dirtyRef = useRef(false);

  const supabaseReady = isSupabaseConfigured();

  // ── History helpers ──────────────────────────────────────────

  const pushHistory = useCallback(
    (newObjects: DrawingObject[]) => {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push([...newObjects]);
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
      }
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      dirtyRef.current = true;
    },
    [history, historyIndex]
  );

  // Handle objects change
  const handleObjectsChange = useCallback(
    (newObjects: DrawingObject[]) => {
      setObjects(newObjects);
      pushHistory(newObjects);
    },
    [pushHistory]
  );

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setObjects([...history[newIndex]]);
      setSelectedObject(null);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setObjects([...history[newIndex]]);
      setSelectedObject(null);
    }
  }, [history, historyIndex]);

  // Handle object update from properties panel
  const handleObjectUpdate = useCallback(
    (updated: DrawingObject) => {
      const newObjects = objects.map((o) => (o.id === updated.id ? updated : o));
      setObjects(newObjects);
      pushHistory(newObjects);
      setSelectedObject(updated);
    },
    [objects, pushHistory]
  );

  // Handle object delete
  const handleObjectDelete = useCallback(
    (id: string) => {
      const newObjects = objects.filter((o) => {
        if (o.id === id) return false;
        if (o.type === 'door' || o.type === 'window') {
          return o.wallId !== id;
        }
        return true;
      });
      setObjects(newObjects);
      pushHistory(newObjects);
      setSelectedObject(null);
    },
    [objects, pushHistory]
  );

  // ── Save / Load ─────────────────────────────────────────────

  const doSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const newId = await saveProjectAuto({
        id: projectId,
        name: projectName,
        objects,
        layers,
        settings: { snapSize, showGrid },
      });
      if (newId) setProjectId(newId);
      setLastSaved(new Date());
      dirtyRef.current = false;
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  }, [projectId, projectName, objects, layers, snapSize, showGrid]);

  /** Load a Supabase project by id */
  const doLoadSupabase = useCallback(async (id: string) => {
    try {
      const project = await loadProject(id);
      setProjectId(project.id);
      setProjectName(project.name);
      setObjects(project.objects);
      setLayers(project.layers);
      setSnapSize(project.settings.snapSize);
      setShowGrid(project.settings.showGrid);
      setHistory([project.objects]);
      setHistoryIndex(0);
      setShowProjectList(false);
    } catch (err) {
      console.error('Load failed:', err);
    }
  }, []);

  /** New blank project */
  const doNewProject = useCallback(() => {
    setProjectId(undefined);
    setProjectName('Nytt projekt');
    setObjects([]);
    setLayers(DEFAULT_LAYERS);
    setSnapSize(DEFAULT_SNAP_SIZE);
    setShowGrid(true);
    setHistory([[]]);
    setHistoryIndex(0);
    setShowProjectList(false);
    dirtyRef.current = true;
  }, []);

  /** Refresh project list from Supabase */
  const refreshProjectList = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const list = await listProjects();
      setProjectList(list);
    } catch (err) {
      console.error('Failed to list projects:', err);
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  /** Delete a project from list */
  const handleDeleteProject = useCallback(
    async (id: string) => {
      if (!confirm('Ta bort detta projekt?')) return;
      try {
        await deleteProject(id);
        setProjectList((prev) => prev.filter((p) => p.id !== id));
        if (projectId === id) doNewProject();
      } catch (err) {
        console.error('Delete failed:', err);
      }
    },
    [projectId, doNewProject]
  );

  // ── Keyboard shortcuts ──────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      if (!modifier) {
        switch (e.key.toLowerCase()) {
          case 'v': setActiveTool('select'); break;
          case 'w': setActiveTool('wall'); break;
          case 'd': setActiveTool('door'); break;
          case 'f': setActiveTool('window'); break;
          case 'e': setActiveTool('erase'); break;
        }
      }

      if (modifier && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if (modifier && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
      if (modifier && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // ── Auto-save (debounced every 30 s after changes) ─────────

  useEffect(() => {
    const interval = setInterval(() => {
      if (dirtyRef.current) {
        doSave();
      }
    }, AUTOSAVE_INTERVAL);
    return () => clearInterval(interval);
  }, [doSave]);

  // ── Initial load ────────────────────────────────────────────

  useEffect(() => {
    // Try loading from localStorage first (always works, even with Supabase)
    const local = loadFromLocalStorage();
    if (local) {
      setProjectId(local.id);
      setProjectName(local.name);
      setObjects(local.objects);
      setLayers(local.layers);
      setSnapSize(local.settings.snapSize);
      setShowGrid(local.settings.showGrid);
      setHistory([local.objects]);
      setHistoryIndex(0);
    }
  }, []);

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="h-screen flex flex-col bg-gray-950">
      {/* Top bar */}
      <header className="h-13 bg-gray-900 border-b border-gray-800 flex items-center px-5 gap-4 shrink-0 shadow-lg shadow-black/20 relative z-10">
        {/* Logo & breadcrumb */}
        <Link href="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
          <div className="w-7 h-7 rounded-lg bg-blue-600/15 border border-blue-500/20 flex items-center justify-center">
            <span className="text-sm">🏗️</span>
          </div>
          <span className="text-gray-400 text-xs font-medium tracking-wide">Bygglov</span>
        </Link>

        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
        </svg>

        {/* Project name */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={projectName}
            onChange={(e) => {
              setProjectName(e.target.value);
              dirtyRef.current = true;
            }}
            className="bg-transparent text-white text-sm font-medium px-2 py-1 rounded border border-transparent hover:border-gray-700 focus:border-blue-500/50 focus:bg-gray-800/50 focus:outline-none transition-all w-40"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={doNewProject}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-md text-xs font-medium transition-colors border border-gray-700"
            title="Nytt projekt"
          >
            ✚ Nytt
          </button>

          {/* Project list (Supabase only) */}
          {supabaseReady && (
            <div className="relative">
              <button
                onClick={() => {
                  const next = !showProjectList;
                  setShowProjectList(next);
                  if (next) refreshProjectList();
                }}
                className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-md text-xs font-medium transition-colors border border-gray-700"
              >
                📂 Projekt
              </button>

              {showProjectList && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl shadow-black/40 z-50 max-h-96 overflow-auto">
                  <div className="p-3 border-b border-gray-700 flex items-center justify-between">
                    <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Projekt</span>
                    <button
                      onClick={() => setShowProjectList(false)}
                      className="text-gray-500 hover:text-white text-sm p-0.5 rounded hover:bg-gray-700 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                  {isLoadingList ? (
                    <div className="p-6 text-center text-gray-400 text-sm">Laddar…</div>
                  ) : projectList.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 text-sm">Inga projekt</div>
                  ) : (
                    <ul>
                      {projectList.map((p) => (
                        <li
                          key={p.id}
                          className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-700/50 cursor-pointer border-b border-gray-700/50 last:border-0 transition-colors"
                          onClick={() => doLoadSupabase(p.id)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm truncate">{p.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {new Date(p.updated_at).toLocaleString('sv-SE')}
                            </div>
                          </div>
                          {projectId === p.id && (
                            <span className="text-[10px] text-blue-400 bg-blue-600/15 px-1.5 py-0.5 rounded">aktiv</span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(p.id);
                            }}
                            className="text-gray-500 hover:text-red-400 text-xs p-1 rounded hover:bg-gray-700 transition-colors"
                            title="Ta bort"
                          >
                            🗑
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1" />

        {/* Object count */}
        <div className="text-xs text-gray-500 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 bg-gray-800/60 px-2 py-1 rounded">
            <span className="text-gray-400">▬</span> {objects.filter((o) => o.type === 'wall').length}
          </span>
          <span className="inline-flex items-center gap-1 bg-gray-800/60 px-2 py-1 rounded">
            <span className="text-gray-400">🚪</span> {objects.filter((o) => o.type === 'door').length}
          </span>
          <span className="inline-flex items-center gap-1 bg-gray-800/60 px-2 py-1 rounded">
            <span className="text-gray-400">◻</span> {objects.filter((o) => o.type === 'window').length}
          </span>
        </div>

        {/* Save button */}
        <button
          onClick={doSave}
          disabled={isSaving}
          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-md text-xs font-semibold disabled:opacity-50 transition-all shadow-sm shadow-blue-600/20"
        >
          {isSaving ? 'Sparar…' : '💾 Spara'}
        </button>

        {lastSaved && (
          <span className="text-[10px] text-gray-500">
            {lastSaved.toLocaleTimeString('sv-SE')}
          </span>
        )}
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left toolbar */}
        <Toolbar
          activeTool={activeTool}
          onToolChange={setActiveTool}
          snapSize={snapSize}
          onSnapChange={setSnapSize}
          showGrid={showGrid}
          onGridToggle={() => setShowGrid(!showGrid)}
          selectedWallType={selectedWallType}
          onWallTypeChange={setSelectedWallType}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history.length - 1}
        />

        {/* Canvas */}
        <div className="flex-1 overflow-hidden">
          <DrawingCanvas
            objects={objects}
            onObjectsChange={handleObjectsChange}
            activeTool={activeTool}
            snapSize={snapSize}
            showGrid={showGrid}
            layers={layers}
            selectedWallType={selectedWallType}
            onSelectedObjectChange={setSelectedObject}
          />
        </div>

        {/* Right properties panel */}
        <PropertiesPanel
          selectedObject={selectedObject}
          onUpdate={handleObjectUpdate}
          onDelete={handleObjectDelete}
        />
      </div>
    </div>
  );
}
